import React, { useCallback, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { Button, RefreshIcon } from '../../lib/components'
import { displayDollar } from '../../lib/utils/helpers'
import { OpportunityDetails } from '../../lib/types'
import { WalletSummary, WalletTopup } from '../../services/wallet-service'
import { BANK_CHARGES_NOTICE } from './invest-v2-share-order'

export type FundStepShareOrder = {
  shares: number
  pricePerShareCents: number
  amountCents: number
  salesFeeCents: number
  finalAmountCents: number
  feePercentLabel?: string
}

type Props = {
  opportunity: OpportunityDetails
  wallet: WalletSummary | undefined
  /** Cents to fund: the final amount (incl. upfront fees) for share-priced orders. */
  investmentAmountCents: number
  shareOrder?: FundStepShareOrder
  pendingTopup: WalletTopup | undefined
  isBusy: boolean
  onPayFromWallet: () => Promise<void>
  onRefresh: () => Promise<void>
  onBack: () => void
}

export const InvestV2WalletFundStep = ({
  opportunity,
  wallet,
  investmentAmountCents,
  shareOrder,
  pendingTopup,
  isBusy,
  onPayFromWallet,
  onRefresh,
  onBack,
}: Props) => {
  const navigate = useNavigate()
  const location = useLocation()
  const [walletRefreshing, setWalletRefreshing] = useState(false)
  const currency = opportunity.currencyCode || 'USD'
  const available = Number(wallet?.available || 0)
  const transferable = Math.min(available, investmentAmountCents)
  const shortfall = Math.max(0, investmentAmountCents - available)
  const canPay = available >= investmentAmountCents
  const awaitingConfirm =
    pendingTopup && (pendingTopup.status === 'ORDER_PLACED' || pendingTopup.status === 'INITIATED')

  const investReturnTo = encodeURIComponent(`${location.pathname}${location.search}`)

  const handleRefreshWallet = useCallback(async () => {
    if (walletRefreshing || isBusy) return
    setWalletRefreshing(true)
    try {
      await onRefresh()
    } finally {
      setWalletRefreshing(false)
    }
  }, [isBusy, onRefresh, walletRefreshing])

  const handlePrimary = () => {
    if (canPay) {
      void onPayFromWallet()
      return
    }
    navigate(`/dashboard/wallet?action=topup&amountCents=${shortfall}&returnTo=${investReturnTo}`)
  }

  const handleViewWallet = () => {
    navigate(`/dashboard/wallet?returnTo=${investReturnTo}`)
  }

  return (
    <section className='invest-v2__card invest-v2__card--fund' data-testid='invest-v2-step-fund'>
      <h1 className='invest-v2__title'>Transfer of Funds</h1>

      {shareOrder ? (
        <div className='invest-v2__fund-summary' data-testid='invest-v2-fund-share-breakdown'>
          <p>
            Investment amount:{' '}
            <strong>
              {displayDollar(String(shareOrder.amountCents), currency, 2, true, true)}
            </strong>
          </p>
          <p>
            Number of shares: <strong>{shareOrder.shares.toLocaleString('en-US')}</strong> at{' '}
            <strong>
              {displayDollar(String(shareOrder.pricePerShareCents), currency, 2, true, true)}
            </strong>{' '}
            per share
          </p>
          <p>
            Upfront Fees{shareOrder.feePercentLabel ? ` (${shareOrder.feePercentLabel}%)` : ''}:{' '}
            <strong>
              {displayDollar(String(shareOrder.salesFeeCents), currency, 2, true, true)}
            </strong>
          </p>
          <p>
            Final Investment amount (incl. fees):{' '}
            <strong data-testid='invest-v2-fund-final-amount'>
              {displayDollar(String(shareOrder.finalAmountCents), currency, 2, true, true)}
            </strong>
          </p>
          {canPay ? null : (
            <p className='invest-v2__fund-notice' data-testid='invest-v2-bank-charges-notice'>
              {BANK_CHARGES_NOTICE}
            </p>
          )}
        </div>
      ) : null}

      <div className='invest-v2__fund-summary'>
        <p className='invest-v2__fund-balance-row'>
          <span>
            Wallet balance:{' '}
            <strong className='invest-v2__fund-balance-value'>
              {displayDollar(String(available), currency, 2, true, true)}
            </strong>
          </span>
          <button
            type='button'
            className='invest-v2__fund-refresh'
            aria-label='Refresh available balance'
            disabled={walletRefreshing || isBusy}
            onClick={() => void handleRefreshWallet()}
            data-testid='invest-v2-refresh-wallet'
          >
            <RefreshIcon
              width='16'
              height='16'
              classes={[
                'invest-v2__fund-refresh-icon',
                ...(walletRefreshing ? ['invest-v2__fund-refresh-icon--spinning'] : []),
              ]}
            />
          </button>
        </p>
        <p>
          Amount available for transfer to the opportunity:{' '}
          <strong data-testid='invest-v2-transferable-amount'>
            {displayDollar(String(transferable), currency, 2, true, true)}
          </strong>
        </p>
        {!canPay ? (
          <p className='invest-v2__fund-shortfall'>
            Additional funds required:{' '}
            <strong>{displayDollar(String(shortfall), currency, 2, true, true)}</strong>
          </p>
        ) : null}
      </div>

      {awaitingConfirm ? (
        <button
          type='button'
          className='invest-v2__link-btn invest-v2__fund-view-wallet'
          onClick={handleViewWallet}
          data-testid='invest-v2-view-wallet'
        >
          View wallet
        </button>
      ) : null}

      <div className='invest-v2__actions invest-v2__actions--split'>
        <button
          type='button'
          className='invest-v2__link-btn'
          onClick={onBack}
          data-testid='invest-v2-back-fund'
        >
          ← Back
        </button>
        <Button
          text={canPay ? 'Complete transaction' : 'Topup wallet'}
          variant='primary-btn'
          isDisabled={isBusy || (Boolean(awaitingConfirm) && !canPay)}
          handleClick={handlePrimary}
          classes={['invest-v2__btn-primary']}
          testId={canPay ? 'invest-v2-pay-from-wallet' : 'invest-v2-topup-wallet'}
        />
      </div>
    </section>
  )
}
